/**
 * Der Rahmen des Leitstands: Kopfleiste, Unterleiste, Bühne.
 *
 * Zwei flache Ebenen statt des heutigen verschachtelten Baums links —
 * oben die drei Bereiche, darunter nur die Seiten des gewählten Bereichs.
 * Welche Seite wo hängt, steht in navigation.ts.
 *
 * Der Bereich wird aus dem Pfad abgeleitet, nicht gespeichert: Wer per
 * Lesezeichen oder Verlauf hereinkommt, landet in der richtigen Ebene.
 */

import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { LeitstandFlaeche } from './LeitstandFlaeche';
import { BEREICHE, findeBereich, findeSeite } from './navigation';
import { Marke } from './index';
import { SchemaSchalter } from './SchemaSchalter';

interface LeitstandRahmenProps {
  /** Überschrift der Seite — steht groß über dem Inhalt */
  titel: string;
  /** Rechts neben der Überschrift, z.B. die Zeitraumwahl */
  werkzeuge?: ReactNode;
  /** Anzeigename des angemeldeten Benutzers */
  benutzer?: string;
  children: ReactNode;
}

/** Kürzel aus dem Namen: "Richard Scherbaum" → "RS" */
function kuerzel(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0]?.toUpperCase() ?? '')
    .join('');
}

export function LeitstandRahmen({ titel, werkzeuge, benutzer, children }: LeitstandRahmenProps) {
  const { pathname } = useLocation();

  // Beim Wechsel des Bereichs zeigt die Unterleiste dessen Seiten, obwohl man
  // die Seite noch nicht gewechselt hat — sonst müsste man raten, was dort liegt.
  const bereichAusPfad = findeBereich(pathname);
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(null);
  const sichtbarerBereich =
    BEREICHE.find((b) => b.schluessel === aufgeklappt) ?? bereichAusPfad ?? BEREICHE[0];

  const aktuelleSeite = findeSeite(pathname);

  return (
    <LeitstandFlaeche>
      <header className="sticky top-0 z-30 flex h-[54px] items-center gap-[22px] border-b border-linie bg-kopf px-5">
        <Link
          to="/reports/twa"
          className="whitespace-nowrap font-zahl text-[13px] font-semibold tracking-[0.06em] text-tinte"
        >
          wash<b className="font-semibold text-akzent">·</b>ops
        </Link>

        <nav className="flex h-full gap-0.5" aria-label="Bereiche">
          {BEREICHE.map((bereich) => {
            const aktiv = bereich.schluessel === sichtbarerBereich.schluessel;
            return (
              <button
                key={bereich.schluessel}
                type="button"
                onClick={() => setAufgeklappt(bereich.schluessel)}
                aria-current={aktiv ? 'page' : undefined}
                className={`relative h-full whitespace-nowrap px-[15px] text-[13.5px] focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-[3px] focus-visible:outline-akzent ${
                  aktiv ? 'text-tinte' : 'text-tinte-2 hover:text-tinte'
                }`}
              >
                {bereich.name}
                {aktiv && (
                  <span
                    className="absolute inset-x-[11px] -bottom-px h-0.5 rounded-t-sm bg-akzent"
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Sprungfeld: im Entwurf vorgesehen, noch nicht gebaut. Sichtbar
            deaktiviert statt versteckt — sonst wirkt die Leiste unfertig,
            und der Platz dafür soll erkennbar bleiben. */}
        <div
          className="ml-auto flex min-w-[210px] cursor-not-allowed items-center gap-[9px] rounded-[5px] border border-linie-2 bg-flaeche px-[11px] py-1.5 text-[13px] text-tinte-3 opacity-60"
          title="Springen und Suchen — in Vorbereitung"
        >
          <span>Suchen oder springen …</span>
          <kbd className="ml-auto rounded-[3px] border border-linie-2 px-[5px] py-px font-zahl text-[10px]">
            ⌘K
          </kbd>
        </div>

        <SchemaSchalter />

        {benutzer && (
          <div className="flex items-center gap-[9px] whitespace-nowrap text-[13px] text-tinte-2">
            <span className="grid h-[27px] w-[27px] place-items-center rounded-full bg-akzent-tief font-zahl text-[11px] font-semibold text-akzent">
              {kuerzel(benutzer)}
            </span>
            <span>{benutzer}</span>
          </div>
        )}
      </header>

      <nav
        className="sticky top-[54px] z-20 flex h-11 items-center gap-[3px] overflow-x-auto border-b border-linie bg-grund px-5"
        aria-label={`Seiten in ${sichtbarerBereich.name}`}
      >
        {sichtbarerBereich.seiten.map((seite) => {
          const aktiv = seite.pfad === aktuelleSeite?.pfad;
          return (
            <Link
              key={seite.pfad}
              to={seite.pfad}
              aria-current={aktiv ? 'page' : undefined}
              className={`whitespace-nowrap rounded-[5px] px-[11px] py-[5px] text-[13px] ${
                aktiv ? 'bg-flaeche-2 text-tinte' : 'text-tinte-3 hover:bg-flaeche hover:text-tinte'
              }`}
            >
              {seite.name}
            </Link>
          );
        })}
      </nav>

      <main className="mx-auto flex max-w-[1240px] flex-col gap-5 px-5 pb-20 pt-[26px]">
        <div className="flex flex-wrap items-end justify-between gap-3.5">
          <div>
            <Marke>{sichtbarerBereich.name}</Marke>
            <h1 className="text-[23px] font-semibold tracking-[-0.01em]">{titel}</h1>
          </div>
          {werkzeuge}
        </div>
        {children}
      </main>
    </LeitstandFlaeche>
  );
}
